import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { MessageSquare, Lightbulb, Bug, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';

const CATEGORIES = [
  { id: 'feature', label: 'Feature Request / Suggestion', icon: Lightbulb, color: 'text-amber-500' },
  { id: 'feedback', label: 'General Feedback', icon: MessageSquare, color: 'text-blue-500' },
  { id: 'bug', label: 'Bug Report / Issue', icon: Bug, color: 'text-red-500' },
];

export default function FeedbackModal({ open, onOpenChange }) {
  const [email, setEmail] = useState('');
  const [category, setCategory] = useState('feedback');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, category, message })
      });
      
      if (res.ok) {
        toast.success('Thank you for your feedback!');
        setEmail('');
        setCategory('feedback');
        setMessage('');
        onOpenChange(false);
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Send Feedback
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Email */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Your Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background"
              data-testid="feedback-email"
            />
          </div>
          
          {/* Category */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Category</label>
            <div className="grid grid-cols-3 gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCategory(cat.id)}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    category === cat.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`feedback-category-${cat.id}`}
                >
                  <cat.icon className={`w-5 h-5 mx-auto mb-1 ${cat.color}`} />
                  <div className="text-xs">{cat.id === 'feature' ? 'Feature' : cat.id === 'bug' ? 'Bug' : 'Feedback'}</div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Message */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Your Message</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's on your mind..."
              rows={4}
              className="resize-none"
              data-testid="feedback-message"
            />
          </div>
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="feedback-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Send Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
